/** The initial UI wait is bounded; the actual Firebase redirect is not cancelled. */
export const INITIAL_AUTH_REDIRECT_WAIT_MS = 4_000;

export type InitialAuthRedirectObservation = "completed" | "deadline" | "failed";

type InitialAuthRedirectMountOptions = {
  resolveRedirect: () => Promise<unknown>;
  renderLoading: () => void;
  renderApp: () => void;
  onRedirectFailure: (error: unknown) => void;
};

/**
 * Mount normal routes after redirect completion or a bounded observation wait.
 * No user, token, claim, or authorization state is supplied by this helper.
 * Keep observing a late rejection so the original operation is never abandoned.
 */
export async function mountWithBoundedAuthRedirect(
  options: InitialAuthRedirectMountOptions,
): Promise<InitialAuthRedirectObservation> {
  options.renderLoading();
  const redirectCompletion = Promise.resolve()
    .then(options.resolveRedirect)
    .then<InitialAuthRedirectObservation, InitialAuthRedirectObservation>(
      () => "completed",
      (error: unknown) => {
        options.onRedirectFailure(error);
        return "failed";
      },
    );
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<InitialAuthRedirectObservation>((resolve) => {
    deadlineTimer = setTimeout(() => resolve("deadline"), INITIAL_AUTH_REDIRECT_WAIT_MS);
  });
  try {
    const observation = await Promise.race([redirectCompletion, deadline]);
    options.renderApp();
    return observation;
  } finally {
    if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
  }
}
